import { createClient } from '@supabase/supabase-js';
import { config } from './config';

// Types de base pour l'authentification
type UserRole = 'admin' | 'editor' | 'viewer';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string | null;
  role: UserRole;
}

// Création du client Supabase avec la configuration
const supabaseUrl = config.supabase.url;
const supabaseAnonKey = config.supabase.anonKey;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase configuration. Please check your .env file.');
}

// Créer le client Supabase sans typage générique pour éviter les conflits
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// Type pour les noms de table
type TableName = keyof Database['public']['Tables'];

// Fonctions utilitaires pour les requêtes courantes
export const db = {
  // Récupérer un élément par son ID
  async findById<T extends TableName>(
    table: T,
    id: string,
    columns = '*'
  ): Promise<Database['public']['Tables'][T]['Row'] | null> {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error fetching ${String(table)} with id ${id}:`, error);
      return null;
    }

    return data;
  },

  // Récupérer tous les éléments avec des options de filtrage
  async findAll<T extends TableName>(
    table: T,
    options: {
      columns?: string;
      filter?: Partial<Database['public']['Tables'][T]['Row']>;
      orderBy?: { column: keyof Database['public']['Tables'][T]['Row']; ascending: boolean };
      limit?: number;
      range?: { from: number; to: number };
    } = {}
  ): Promise<Database['public']['Tables'][T]['Row'][]> {
    let query = supabase
      .from(table)
      .select(options.columns || '*', { count: 'exact' });

    // Appliquer les filtres
    if (options.filter) {
      Object.entries(options.filter).forEach(([key, value]) => {
        if (value !== undefined) {
          query = query.eq(key, value);
        }
      });
    }

    // Trier les résultats
    if (options.orderBy) {
      query = query.order(options.orderBy.column as string, {
        ascending: options.orderBy.ascending,
      });
    }

    // Limiter les résultats
    if (options.limit) {
      query = query.limit(options.limit);
    }

    // Pagination
    if (options.range) {
      query = query.range(options.range.from, options.range.to);
    }

    const { data, error } = await query;

    if (error) {
      console.error(`Error fetching ${String(table)}:`, error);
      return [];
    }

    return data || [];
  },

  // Créer un nouvel élément
  async create<T extends TableName>(
    table: T,
    data: Omit<Database['public']['Tables'][T]['Insert'], 'id' | 'created_at' | 'updated_at'>
  ): Promise<Database['public']['Tables'][T]['Row'] | null> {
    const { data: result, error } = await supabase
      .from(table)
      .insert([data])
      .select()
      .single();

    if (error) {
      console.error(`Error creating ${String(table)}:`, error);
      return null;
    }

    return result;
  },

  // Mettre à jour un élément
  async update<T extends TableName>(
    table: T,
    id: string,
    updates: Partial<Database['public']['Tables'][T]['Update']>
  ): Promise<Database['public']['Tables'][T]['Row'] | null> {
    const { data, error } = await supabase
      .from(table)
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating ${String(table)} with id ${id}:`, error);
      return null;
    }

    return data;
  },

  // Supprimer un élément
  async delete<T extends TableName>(table: T, id: string): Promise<boolean> {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Error deleting ${String(table)} with id ${id}:`, error);
      return false;
    }

    return true;
  },

  // Compter le nombre d'éléments
  async count<T extends TableName>(
    table: T,
    filter?: Partial<Database['public']['Tables'][T]['Row']>
  ): Promise<number> {
    let query = supabase
      .from(table)
      .select('*', { count: 'exact', head: true });

    // Appliquer les filtres
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined) {
          query = query.eq(key, value);
        }
      });
    }

    const { count, error } = await query;

    if (error) {
      console.error(`Error counting ${String(table)}:`, error);
      return 0;
    }

    return count || 0;
  },

  // Exécuter une requête personnalisée
  async rpc<T = any>(
    fn: string,
    params?: Record<string, unknown>
  ): Promise<T | null> {
    const { data, error } = await supabase.rpc(fn, params);

    if (error) {
      console.error(`Error executing RPC ${fn}:`, error);
      return null;
    }

    return data;
  },
};

// Exporter les types pour une utilisation facile
export type { UserRole, UserProfile };

// Exporter les types de base pour une utilisation dans les composants
export type { Tables } from '@/types/database';
